import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  ApexChart,
  ApexAxisChartSeries,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexYAxis,
  ApexLegend,
  ApexGrid,
  ApexNonAxisChartSeries,
  ApexXAxis
} from "ng-apexcharts";
import { ResidencesService } from '../../core/services/residences.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, PropertyAvailability, Residences, ResidencesResponse, SalesMetric, SalesPercentage, SalesRevenue } from '../../core/models/residences';
import { SortConfig } from '../../core/interface/SortConfig';
import { PaginationConfig } from '../../core/interface/PaginationConfig';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

export type ChartOptions = {
  series: ApexAxisChartSeries;  // Hapus union type dengan undefined
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  grid: ApexGrid;
  colors: string[];
  legend: ApexLegend;
};

@Component({
  selector: 'app-residences',
  templateUrl: './residences.component.html',
  styleUrl: './residences.component.css'
})
export class ResidencesComponent implements OnInit {

  @ViewChild("chart") chart!: ChartComponent;
  public chartOptions!: ChartOptions;
  private resizeListener!: () => void;

  expandedStates: boolean[] = [];
  isTotalExpanded: boolean = false;

  // Index untuk setiap section
  totalIndex: number = -1;
  pchIndex: number = -1;
  LHIndex: number = -1;

  residence: Residences[] = [];
  itemAvailablity: PropertyAvailability[] = []
  itemSalesRevenue: SalesRevenue = {} as SalesRevenue
  itemSalesPercentage: SalesPercentage = {} as SalesPercentage
  itemSalesMetric: SalesMetric = {} as SalesMetric
  metaData: Meta = {} as Meta
  maxLength: number = 0
  isLoading = false;
  error = '';

  // Filters
  searchForm !: FormGroup;
  sortBy = '';
  startDate: string = '';
  endDate: string = ''

  sortConfig: SortConfig = {
    column: '',
    direction: ''
  };

  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  };

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  isLoadingSalesMetric = false;

  constructor(
    private residencesService: ResidencesService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {

    this.setupSearchSubscription();
    this.searchForm = this.fb.group({
      searchTerm: ['']
    });
  }

  private setupSearchSubscription(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Update URL dan load data hanya jika ada term
      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadResidencesData(term);
    });
  }


  ngOnInit() {
    this.loadDatesFromLocalStorage();
    // this.loadAllData();
    this.subscribeToRouteParams();
    this.setupResizeListener(); // Setup resize listener for chart responsiveness
  }

  toggleTotal() {
    console.log('=== TOGGLE TOTAL DIPANGGIL ===');
    console.log('Sebelum:', {
        isTotalExpanded: this.isTotalExpanded,
        expandedStates: [...this.expandedStates]
    });
    
    this.isTotalExpanded = !this.isTotalExpanded;
    
    console.log('Setelah toggle:', {
        isTotalExpanded: this.isTotalExpanded,
        akan_set_semua_ke: this.isTotalExpanded
    });
    // Expand/collapse semua section
    if (this.pchIndex !== -1) {
        this.expandedStates[this.pchIndex] = this.isTotalExpanded;
        console.log(`PCH (index ${this.pchIndex}) → ${this.isTotalExpanded}`);
    }
    if (this.LHIndex !== -1) {
        this.expandedStates[this.LHIndex] = this.isTotalExpanded;
        console.log(`LH (index ${this.LHIndex}) → ${this.isTotalExpanded}`);
    }
    console.log('Akhir:', {
        isTotalExpanded: this.isTotalExpanded,
        expandedStates: [...this.expandedStates]
    });
  }

  // Toggle individual section
  toggleExpand(index: number) {
    this.expandedStates[index] = !this.expandedStates[index];
  }

  private loadDatesFromLocalStorage(): void {
    const startDate = localStorage.getItem('startDate');
    const endDate = localStorage.getItem('endDate');

    this.startDate = startDate || '';
    this.endDate = endDate || '';
  }

  private loadAllData(): void {
    this.loadResidenceAvailablity();
    this.loadSalesRevenue();
    this.loadSalesPercentage();
    this.loadSalesMetric();
  }

  private subscribeToRouteParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.searchTerm = params['search'] || '';
        this.sortConfig.column = params['sortColumn'] || '';
        this.sortConfig.direction = params['sortDirection'] as 'ASC' | 'DESC' | '' || '';
        this.paginationConfig.currentPage = Number(params['page']) || 1;

        this.loadResidencesData(this.searchTerm);
      });
  }

  // Method untuk refresh data (ketika filter tanggal berubah)
  public refreshDashboardData(): void {

    // Update localStorage jika diperlukan
    if (this.startDate) localStorage.setItem('startDate', this.startDate);
    if (this.endDate) localStorage.setItem('endDate', this.endDate);

    // Reload data
    this.loadAllData();
  }

  ngAfterViewInit(): void {
    // Initialize the resize handler after view is initialized
    this.handleChartResize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  loadResidenceAvailablity(): void {
    this.isLoading = true;

    this.residencesService.getResidencePropertyAvailablity(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.itemAvailablity = response.data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading residence availablity data:', error);
        this.isLoading = false;
        this.itemAvailablity = [];
      }
    });
  }

  loadSalesMetric(): void {
    this.isLoadingSalesMetric = true;

    this.residencesService.getSalesMetric(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.itemSalesMetric = response.data;
        this.maxLength = response.data.max_pct_value;
        this.metaData = response.meta;
        this.initChartData(response.data.chart, this.maxLength);

        this.isLoadingSalesMetric = false;
        // console.log('Sales Metric Data:', this.itemSalesMetric);
      },
      error: (error) => {
        console.error('Error loading sales metric data:', error);
        this.itemSalesMetric = {} as SalesMetric;
        this.initChartData([], 0); // Reset chart data on error
        this.isLoadingSalesMetric = false;
      }
    });
  }

  loadSalesRevenue(): void {
    this.isLoading = true;

    this.residencesService.getSalesRevenue(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.itemSalesRevenue = response.data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading residence availablity data:', error);
        this.isLoading = false;
        this.itemSalesRevenue = {} as SalesRevenue;
      }
    });
  }

  loadSalesPercentage(): void {
    this.isLoading = true;

    this.residencesService.getSalesPercentage(
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        this.itemSalesPercentage = response.data;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading sales percentage data:', error);
        this.isLoading = false;
        this.itemSalesPercentage = {} as SalesPercentage;
      }
    });
  }


  // Metode yang ditingkatkan dengan penanganan error lebih detail
  loadResidencesData(searchTerm?: string): void {
    this.isLoading = true;
    this.error = ''; // Reset error sebelumnya

    this.residencesService.getListResidences(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.sortConfig.column,
      this.sortConfig.direction ? this.sortConfig.direction.toUpperCase() as 'ASC' | 'DESC' : undefined,
      this.startDate,
      this.endDate
    ).subscribe({
      next: (response) => {
        if (response && response.data) {
          this.residence = response.data;

          if (response.pagination) {
            this.paginationConfig.totalItems = response.pagination.total_items;
            this.paginationConfig.currentPage = response.pagination.current_page;
          }

          this.isLoading = false;
        } else {
          this.error = 'Respons API tidak valid';
          this.isLoading = false;
          this.residence = [];
        }
      },
      error: (error) => {
        let errorMessage = 'Error saat memuat data residences: ';

        if (error.status === 200 && error.statusText === 'OK') {
          errorMessage += 'Error saat memproses format respons JSON';
        } else if (error.error instanceof ErrorEvent) {
          // Error dari sisi client
          errorMessage += `Error client: ${error.error.message}`;
        } else {
          // Error dari sisi server
          errorMessage += `Kode: ${error.status}, Pesan: ${error.message}`;
        }

        console.error(errorMessage, error);
        this.error = errorMessage;
        this.isLoading = false;
        this.residence = [];
      }
    });
  }

  onSearch(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const term = target.value;

    // Update searchTerm dan trigger pencarian
    this.searchTerm = term;
    this.paginationConfig.currentPage = 1;
    this.searchSubject.next(term);

    // Jika input kosong, langsung clear search dan reset data
    if (!term.trim()) {
      const queryParams: Record<string, string | number | null> = {
        page: 1,
        search: null
      };

      // Jika ada sort config, pertahankan
      if (this.sortConfig.column && this.sortConfig.direction) {
        queryParams['sortColumn'] = this.sortConfig.column;
        queryParams['sortDirection'] = this.sortConfig.direction;
      }

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true
      });

      this.loadResidencesData('');
    }
  }

  onSort(column: string): void {
    if (this.sortConfig.column === column) {
      this.sortConfig.direction = this.sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortConfig.column = column;
      this.sortConfig.direction = 'ASC';
    }

    this.paginationConfig.currentPage = 1; // Reset to first page on sort
    this.updateUrlParams();
    this.loadResidencesData(this.searchTerm);
  }

  getSortIcon(column: string): string {
    // Jika bukan kolom yang sedang di-sort, tampilkan icon default
    if (this.sortConfig.column !== column) {
      return 'ri-expand-up-down-line'; // Remix icon untuk unsorted
    }

    // Jika kolom yang sedang di-sort
    if (this.sortConfig.direction === 'ASC') {
      return 'ri-arrow-up-line'; // Remix icon arrow up
    } else if (this.sortConfig.direction === 'DESC') {
      return 'ri-arrow-down-line'; // Remix icon arrow down
    }

    return 'ri-expand-up-down-line'; // Default fallback
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.paginationConfig.currentPage
    };

    // Hanya tambahkan search param jika ada nilai
    if (this.searchTerm?.trim()) {
      queryParams.search = this.searchTerm.trim();
    } else {
      queryParams.search = null; // Hapus search param
    }

    if (this.sortConfig.column && this.sortConfig.direction) {
      queryParams.sortColumn = this.sortConfig.column;
      queryParams.sortDirection = this.sortConfig.direction;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  get totalPages(): number {
    return Math.ceil(this.paginationConfig.totalItems / this.paginationConfig.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const currentPage = this.paginationConfig.currentPage;
    const totalPages = this.totalPages;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  get displayRange(): { start: number; end: number; total: number } {
    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.itemsPerPage + 1;
    const end = Math.min(
      start + this.paginationConfig.itemsPerPage - 1,
      this.paginationConfig.totalItems
    );

    return {
      start: this.paginationConfig.totalItems === 0 ? 0 : start,
      end,
      total: this.paginationConfig.totalItems
    };
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.paginationConfig.currentPage = 1;
    this.updateUrlParams();
    this.loadResidencesData('');
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.paginationConfig.currentPage) {
      return;
    }

    this.paginationConfig.currentPage = page;
    this.updateUrlParams();
    this.loadResidencesData(this.searchTerm);
  }


  onFirstPage(): void {
    this.onPageChange(1);
  }

  onLastPage(): void {
    this.onPageChange(this.totalPages);
  }


  onPrevPage(): void {
    this.onPageChange(this.paginationConfig.currentPage - 1);
  }

  onNextPage(): void {
    this.onPageChange(this.paginationConfig.currentPage + 1);
  }


  private initChartData(chartData: any[], maxLength: any): void {
    // Ekstrak value untuk series data
    const seriesData = chartData.map(item => item.amount);
    const formattedDates = chartData.map(item => item.trans_date);

    console.log(formattedDates)


    // Logika untuk warna - default semua #B6D1C1, yang terakhir #191919
    const xaxisLabelColors = Array(chartData.length).fill("#B6D1C1");
    if (chartData.length > 0) {
      xaxisLabelColors[chartData.length - 1] = "#191919";
    }

    this.chartOptions = {
      series: [{
        name: "Persentase",
        data: seriesData,
      }],
      chart: {
        height: 400,
        width: '100%',
        type: "bar",
        events: {
          click: function (event, chartContext, config) {
            const defaultColors = Array(chartData.length).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },

          mouseMove: function (event, chartContext, config) {
            const defaultColors = Array(chartData.length).fill("#B6D1C1");

            if (config.dataPointIndex !== -1) {
              defaultColors[config.dataPointIndex] = "#126936";
            }

            chartContext.updateOptions({
              colors: defaultColors
            }, false, false);
          },
        },
        zoom: {
          enabled: false
        },
        toolbar: {
          show: false
        },
      },
      colors: Array(chartData.length).fill("#B6D1C1"),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          distributed: true,
          dataLabels: {
            position: 'top'
          }
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val + "%"
        },
        offsetY: -25,
        style: {
          fontSize: '12px',
          colors: ["#126936"],
          fontWeight: 'bold'
        },
        distributed: true,
      },
      legend: {
        show: false
      },
      grid: {
        show: true,
        position: 'back',
        borderColor: '#E6E6E6',
        xaxis: {
          lines: {
            show: false
          },
        },
        yaxis: {
          lines: {
            show: false,
          }
        },
        padding: {
          left: 20
        }
      },
      xaxis: {
        categories: formattedDates,
        labels: {
          rotate: -45,
          trim: false,
          hideOverlappingLabels: false,
          rotateAlways: true,
          style: {
            colors: xaxisLabelColors,
            cssClass: 'apexcharts-xaxis-label',
            fontSize: "12px",
            fontWeight: 'bold'
          },
          offsetY: 8,
          offsetX: 10
        },
        tickPlacement: 'on',
      },
      yaxis: {
        title: {
          text: ""
        },
        min: 0,
        max: maxLength, // Gunakan nilai maksimum yang dihitung
        tickAmount: 5,
        labels: {
          formatter: function (val) {
            return val + "%"
          },
          style: {
            colors: '#666666',
            fontSize: '12px',
            fontWeight: 'normal'
          },
          offsetX: 10
        },
        axisBorder: {
          show: true,
          color: "#E6E6E6"
        },
        axisTicks: {
          show: true,
          color: "#E6E6E6",
          width: 6,
          offsetX: 0
        },
        crosshairs: {
          show: false
        },
        opposite: false
      },
    };
  }

  // Add these methods to your component
  private setupResizeListener(): void {
    window.addEventListener('resize', this.handleChartResize.bind(this));

    // Clean up listener when component is destroyed
    this.ngOnDestroy = () => {
      window.removeEventListener('resize', this.handleChartResize.bind(this));
    };
  }

  private handleChartResize(): void {
    setTimeout(() => {
      const chartContainer = document.querySelector('.chart-container') as HTMLElement;
      const chartWrapper = document.querySelector('.chart-wrapper') as HTMLElement;

      if (chartContainer && chartWrapper) {
        const windowWidth = window.innerWidth;

        // Determine if scrolling is needed based on screen size and content
        if (windowWidth < 768 && this.chartOptions.series[0].data.length > 5) {
          // For mobile with many data points, enable horizontal scrolling
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 70) + 'px';
        } else if (windowWidth >= 768 && windowWidth < 992 && this.chartOptions.series[0].data.length > 7) {
          // For tablets with many data points
          chartWrapper.classList.add('enable-scroll');
          chartContainer.style.minWidth = (this.chartOptions.series[0].data.length * 60) + 'px';
        } else {
          // For desktop or fewer data points, disable scrolling
          chartWrapper.classList.remove('enable-scroll');
          chartContainer.style.minWidth = '100%';
        }
      }
    }, 100);
  }
  // Method untuk menghitung total revenue
  getTotalRevenue(): number {
      if (!this.residence || this.residence.length === 0) {
          return 0;
      }
      
      return this.residence.reduce((total, item) => {
          return total + (item.sales_revenue || 0);
      }, 0);
  }
  get totalRevenueOnly(): Residences[] {
        return this.residence.filter(item => item.property_name === 'Total');
      }
    getPercentageClass(percentage: number): string {
      if (percentage >= 100) {
        return 'badge custom-progress-success';
      }
      return 'badge custom-progress-danger';
    }
}


